import { useAtsConversationApi } from '@/composables/useApi';

import type { UnreadMessageCount } from '@/core/activity-feed/interfaces/unread-message-count.interface';
import type { GetUnreadMessagesResponse } from '@/core/activity-feed/interfaces/unread-message.interface';
import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import { InternalError } from '@/core/shared/errors/internal.error';

export class ActivityFeedPersistence {
  public async getUnreadMessageCount(employerId: number): Promise<number> {
    const url = `/message/employer/${employerId}/unread-messages/count`;

    const { data, error, statusCode } = await useAtsConversationApi(url)
      .get()
      .json<UnreadMessageCount>();

    if (statusCode.value === HttpStatus.FORBIDDEN) {
      return 0;
    }

    if (error.value) {
      throw new InternalError('Could not fetch unread message count', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }

    if (!data.value || typeof data.value.unreadCount === 'undefined') {
      throw new Error('No unread messages found');
    }

    return data.value.unreadCount;
  }

  public async getUnreadMessages(
    employerId: number,
    page: number,
    itemsPerPage: number,
  ): Promise<GetUnreadMessagesResponse> {
    const url = `/message/employer/${employerId}/unread-messages?page=${page}&itemsPerPage=${itemsPerPage}`;

    const { data, error, statusCode } = await useAtsConversationApi(url)
      .get()
      .json<GetUnreadMessagesResponse>();

    if (error.value) {
      throw new InternalError('Could not fetch unread messages', {
        error: error.value,
        status: statusCode.value,
        data: { page, itemsPerPage, employerId },
      });
    }

    if (!data.value) {
      throw new Error('No unread messages found');
    }

    return data.value;
  }

  public async markAllAsRead(employerId: number): Promise<void> {
    const url = `/message/employer/${employerId}/mark-all-read`;

    const { error, statusCode } = await useAtsConversationApi(url).post();

    if (error.value) {
      throw new InternalError('Could not mark all messages as read', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }
  }
}
